import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";

import { CommonTableCellProps } from "./types";

import styles from "./TableCells.module.scss";

/**
 * The common props for the TableHeaderCell component
 */
export type CommonTableHeaderCellProps = CommonTableCellProps & {
  /**
   * Whether the cell is a group cell (for grouped columns)
   */
  isGroup?: boolean;
  /**
   * Whether the header cell can be used to activate column sorting
   */
  isSortable?: boolean;
  /**
   * Whether the header cell should show whole-cell interactive affordances
   */
  isInteractiveHeaderCell?: boolean;
};

export type SimpleTableHeaderCellProps = CommonTableHeaderCellProps &
  ComponentPropsWithoutRef<"th">;

export type DataTableHeaderCellProps = CommonTableHeaderCellProps &
  ComponentPropsWithoutRef<"div">;

/**
 * The props for the TableHeaderCell component
 */
export type TableHeaderCellProps =
  | ({
      /**
       * The type of table being rendered
       */
      type: "simple";
    } & SimpleTableHeaderCellProps)
  | ({
      /**
       * The type of table being rendered
       */
      type: "data-table";
    } & DataTableHeaderCellProps);

/**
 * The styled table header cell component
 * @param props - The props for the TableHeaderCell component
 * @param props.type - The type of table being rendered
 * @param props.children - The children to render in the cell
 * @param props.className - The class name of the TableHeaderCell component
 * @param props.rest - The rest of the props
 */
export const TableHeaderCell = forwardRef<
  HTMLTableCellElement | HTMLDivElement,
  TableHeaderCellProps
>(
  (
    {
      type,
      children,
      className,
      isGroup,
      isSortable,
      isInteractiveHeaderCell,
      isSelectCell,
      isExpandCell,
      ...rest
    },
    ref,
  ) => {
    const classes = cx(styles["table-header-cell"], className, {
      [styles["table-header-cell-group"]]: isGroup,
      [styles["table-header-cell-sortable"]]: isSortable,
      [styles["table-header-cell-interactive"]]: isInteractiveHeaderCell,
      [styles["table-cell-internal-expand"]]: isExpandCell,
      [styles["table-cell-internal-select"]]: isSelectCell,
    });
    const CellElement = type === "simple" ? "th" : "div";

    return (
      <CellElement
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- could be table cell or div
        ref={ref as any}
        data-anv="table-header-cell"
        {...rest}
        className={classes}
        role={type === "simple" ? undefined : "columnheader"}
      >
        {children}
      </CellElement>
    );
  },
);

TableHeaderCell.displayName = "TableHeaderCell";
